/**
 * WordRush sound.
 * ===============
 *
 * NINE sounds. They are real files (`public/sounds/*.wav`), synthesised by
 * `scripts/sound/build-sounds.mjs` and committed alongside it.
 *
 * The service this replaces declared 23 sounds against `/sounds/*.mp3`, and
 * `public/sounds/` did not exist. Six were synthesised at runtime; every other
 * `play()` was a silent no-op, and the Settings toggle governed a nearly-mute
 * system. Deleting the 14 fictional sounds is most of this file's diff.
 *
 * THE ONE-ROOM RULE
 * -----------------
 * This is a game six people play in one room, on six phones. Six phones playing
 * the same sound 40ms apart is not "louder", it is a mess. So every sound is
 * either PERSONAL or SHARED:
 *
 *   PERSONAL — tap, scribble, lockin, notify.
 *       Fired by THIS device's own actions. Your tap, your typing, your lock-in,
 *       your invite. They cannot chorus, because nobody else's phone is doing
 *       the thing that caused them. No gating needed.
 *
 *   SHARED — spin, reveal, tick, pop, chord.
 *       Every phone in the room fires these at the same instant, off the same
 *       server event. That is the chorus risk, and it is handled in one place:
 *       `shouldSuppress()`.
 *         · A TV is connected  → phones go SILENT for shared sounds and the TV
 *           plays them (×1.3), because a room with a screen has a speaker and
 *           the sound should come from where the eyes are.
 *         · No TV              → phones DO play them. That is fine: they are all
 *           short and dry — no reverb tails, no melodies — so six phones 40ms
 *           apart reads as one slightly wide sound.
 *         · Except the CHORD, which is 1.2s and pitched, and ×6 reads as a
 *           smear. It is staggered by hash(playerId) % 250ms so the room
 *           strums instead.
 */

/* ------------------------------------------------------------------ */
/* The nine                                                            */
/* ------------------------------------------------------------------ */

export type SoundKey =
  | 'tap'       // 1 — primary/dark button press
  | 'spin'      // 2 — the wheel starts
  | 'reveal'    // 3 — the wheel settles on the letter
  | 'scribble'  // 4 — a keystroke in a race field
  | 'lockin'    // 5 — YOUR OWN "I'M DONE"
  | 'tick'      // 6 — each of the last five seconds
  | 'pop'       // 7 — a tally row lands
  | 'chord'     // 8 — the winner
  | 'notify';   // 9 — an invite, a toast

interface SoundDef {
  /** Mix level from the spec. Multiplied by the user's master volume. */
  volume: number;
  /**
   * SHARED sounds fire on every phone at once off the same server event, so
   * they are the ones a TV has to take over. PERSONAL sounds are caused by this
   * device and can never chorus.
   */
  shared: boolean;
}

const SOUNDS: Record<SoundKey, SoundDef> = {
  tap:      { volume: 0.40, shared: false },
  spin:     { volume: 0.50, shared: true  },
  reveal:   { volume: 0.60, shared: true  },
  scribble: { volume: 0.25, shared: false },
  lockin:   { volume: 0.60, shared: false },
  tick:     { volume: 0.35, shared: true  },
  pop:      { volume: 0.40, shared: true  },
  chord:    { volume: 0.70, shared: true  },
  notify:   { volume: 0.50, shared: false },
};

export const SOUND_KEYS = Object.keys(SOUNDS) as SoundKey[];

/** The TV is across the room, not in your hand. */
const TV_GAIN = 1.3;

/** Pitch ceiling for the tally pops: 1.0 → 1.5 is roughly seven semitones. */
const POP_MAX_RATE = 1.5;
/** After 8 rows the ladder stops climbing — beyond that it is a kettle. */
const POP_MAX_STEPS = 8;
/** A gap this long means a new category, so the ladder resets. */
const POP_RESET_MS = 1500;

interface SoundConfig {
  /** The master switch. One toggle, and it means it. */
  enabled: boolean;
  volume: number;
  hapticEnabled: boolean;
  /**
   * "Play sounds when casting to a TV." Default OFF — the whole point of a TV
   * is that the room's audio comes from the TV. On, for the person who wants
   * their phone to keep chirping anyway.
   */
  playWhenCasting: boolean;
}

const CONFIG_KEY = 'wordrush_sound_config';

const DEFAULT_CONFIG: SoundConfig = {
  enabled: true,
  volume: 0.7,
  hapticEnabled: true,
  playWhenCasting: false,
};

/* ------------------------------------------------------------------ */
/* Service                                                             */
/* ------------------------------------------------------------------ */

class SoundService {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers = new Map<SoundKey, AudioBuffer>();
  /** In-flight (or settled) load per sound. One fetch per file, ever. */
  private loaders = new Map<SoundKey, Promise<void>>();
  private config: SoundConfig = { ...DEFAULT_CONFIG };

  /** A TV observer is watching this room ⇒ phones hand the shared sounds over. */
  private tvConnected = false;

  /**
   * An explicit answer to "am I the TV?", if anyone has given one. Otherwise we
   * read the ROUTE — which means the TV canvas screen (owned by another branch)
   * needs no edit at all to be treated as the room's speaker.
   */
  private tvModeOverride: boolean | null = null;

  /** Read live, not once at construction: the app is an SPA, and a client-side
   *  navigation into /tv doesn't reconstruct this service. */
  private get isTv(): boolean {
    if (this.tvModeOverride !== null) return this.tvModeOverride;
    if (typeof window === 'undefined') return false;
    return window.location?.pathname?.startsWith('/tv') ?? false;
  }

  /** The tally pop ladder: how many pops in the current run, and when the last one was. */
  private popStep = 0;
  private popLastAt = 0;

  /** Per-field throttle stamps for `scribble`. */
  private throttles = new Map<string, number>();

  /** The one long sound: a new spin has to be able to stop the previous one. */
  private spinSource: AudioBufferSourceNode | null = null;

  constructor() {
    this.loadConfig();
    if (typeof window !== 'undefined') this.armUnlock();
  }

  /* ---------------- config ---------------- */

  private loadConfig() {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = localStorage.getItem(CONFIG_KEY);
      if (saved) this.config = { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      // The Settings screen has always written these two keys directly, and
      // they predate this file. Honour them so an existing user's "sound off"
      // survives the rewrite.
      if (localStorage.getItem('wr.sound') === 'off') this.config.enabled = false;
      if (localStorage.getItem('wr.haptics') === 'off') this.config.hapticEnabled = false;
    } catch {
      this.config = { ...DEFAULT_CONFIG };
    }
  }

  private saveConfig() {
    try { localStorage.setItem(CONFIG_KEY, JSON.stringify(this.config)); } catch {}
  }

  getConfig(): SoundConfig {
    return { ...this.config };
  }

  setEnabled(enabled: boolean) {
    this.config.enabled = enabled;
    this.saveConfig();
    // Turning sound ON is itself a user gesture — take the opportunity.
    if (enabled) void this.unlock();
  }

  setVolume(volume: number) {
    this.config.volume = Math.max(0, Math.min(1, volume));
    this.saveConfig();
    if (this.master && this.ctx) {
      this.master.gain.setValueAtTime(this.config.volume, this.ctx.currentTime);
    }
  }

  setHapticEnabled(enabled: boolean) {
    this.config.hapticEnabled = enabled;
    this.saveConfig();
  }

  setPlayWhenCasting(enabled: boolean) {
    this.config.playWhenCasting = enabled;
    this.saveConfig();
  }

  /* ---------------- the TV ---------------- */

  /**
   * Consumes the `tvConnected` room-state flag / `observer_joined` broadcast.
   * Until that ships, nothing calls this and the flag stays false — which
   * degrades to "no TV", i.e. phones keep playing the shared sounds. The
   * fallback is the status quo, never silence.
   */
  setTvConnected(connected: boolean) {
    this.tvConnected = connected;
  }

  isTvConnected() {
    return this.tvConnected;
  }

  /** For the TV canvas, if it ever wants to say so explicitly rather than by route. */
  setTvMode(isTv: boolean) {
    this.tvModeOverride = isTv;
  }

  /**
   * The one-room rule, in one function.
   *
   * Personal sounds are never suppressed — they are yours, and no other phone
   * is making them. A shared sound is suppressed on a PHONE when a TV has the
   * room (unless the user has explicitly asked to keep hearing their phone).
   * On the TV itself, nothing is suppressed: it is the room's speaker now.
   */
  private shouldSuppress(key: SoundKey): boolean {
    if (this.isTv) return false;
    if (!SOUNDS[key].shared) return false;
    if (!this.tvConnected) return false;
    return !this.config.playWhenCasting;
  }

  /* ---------------- unlocking + lazy load ---------------- */

  /**
   * A browser will not play a sound before a user gesture, and Safari will not
   * even let the AudioContext leave 'suspended'. So: create nothing on page
   * load, and on the FIRST real gesture create the context, resume it, and only
   * then fetch the audio. The files are ~169KB and nobody who never taps
   * anything should pay for them.
   */
  private armUnlock() {
    const events: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
    const onGesture = () => {
      void this.unlock();
      events.forEach(e => document.removeEventListener(e, onGesture));
    };
    events.forEach(e => document.addEventListener(e, onGesture, { passive: true }));
  }

  /** Idempotent. Safe to call from anywhere that knows a gesture just happened. */
  async unlock(): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.config.volume;
        this.master.connect(this.ctx.destination);
      }
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      void this.preload();
    } catch (err) {
      console.warn('[sound] could not unlock audio', err);
    }
  }

  /**
   * Fetch + decode ONE sound, once. Per-key, not all-or-nothing: the 1.3KB
   * `tap` must not have to wait behind the 99KB `spin` to become audible — that
   * is the difference between a first click that responds and a first click
   * that doesn't.
   *
   * Failures are logged, never thrown. A missing sound file must not be able to
   * break a round.
   */
  private loadOne(key: SoundKey): Promise<void> {
    const existing = this.loaders.get(key);
    if (existing) return existing;
    if (!this.ctx) return Promise.resolve();
    const ctx = this.ctx;

    const p = (async () => {
      try {
        const res = await fetch(soundUrl(key));
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.buffers.set(key, buf);
      } catch (err) {
        // A 404 here IS the bug this file was rewritten to kill. Be loud.
        console.error(`[sound] failed to load ${soundUrl(key)}`, err);
        this.loaders.delete(key); // let a later gesture try again
      }
    })();

    this.loaders.set(key, p);
    return p;
  }

  /** All nine. Kicked off by the first gesture; safe to call repeatedly. */
  preload(): Promise<void> {
    return Promise.all(SOUND_KEYS.map(k => this.loadOne(k))).then(() => void 0);
  }

  /* ---------------- play ---------------- */

  /**
   * @param opts.rate       playbackRate — the pop ladder uses this to transpose.
   * @param opts.delayMs    schedule ahead (the winner chord's strum stagger).
   * @param opts.throttleMs collapse repeats within this window…
   * @param opts.throttleKey …per key (one throttle bucket PER FIELD, so typing
   *                         in Name does not silence typing in Place).
   */
  play(
    key: SoundKey,
    opts: { rate?: number; delayMs?: number; throttleMs?: number; throttleKey?: string } = {},
  ) {
    if (!this.config.enabled) return;
    if (this.shouldSuppress(key)) return;

    if (opts.throttleMs) {
      const bucket = `${key}:${opts.throttleKey ?? ''}`;
      const now = Date.now();
      const last = this.throttles.get(bucket) ?? 0;
      if (now - last < opts.throttleMs) return;
      this.throttles.set(bucket, now);
    }

    // Not unlocked yet ⇒ no gesture has happened ⇒ nothing may play anyway.
    if (!this.ctx || !this.master) return;
    const buffer = this.buffers.get(key);
    if (!buffer) {
      /**
       * The audio is still decoding — which is ALWAYS true for the very first
       * sound of a session, because the gesture that asks for it is the same
       * gesture that starts the download. Dropping it outright would mean your
       * first button press is the one press that makes no sound, in the feature
       * whose entire purpose is that things make sounds.
       *
       * So: wait for the load, and play it IF it arrives while the moment is
       * still current. Past ~250ms a UI sound is no longer feedback, it is a
       * ghost — so past 250ms we drop it after all. This retries once and never
       * queues.
       */
      const asked = Date.now();
      void this.loadOne(key).then(() => {
        if (Date.now() - asked > 250) return;
        if (!this.buffers.has(key)) return; // the fetch failed; loadOne said so
        // Throttle already stamped on the way in — don't let the retry re-check
        // it and reject itself.
        this.play(key, { ...opts, throttleMs: undefined });
      });
      return;
    }

    try {
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      if (opts.rate) src.playbackRate.value = opts.rate;

      const gain = this.ctx.createGain();
      gain.gain.value = SOUNDS[key].volume * (this.isTv ? TV_GAIN : 1);

      src.connect(gain);
      gain.connect(this.master);
      src.start(this.ctx.currentTime + (opts.delayMs ?? 0) / 1000);

      if (key === 'spin') {
        this.spinSource = src;
        src.onended = () => { if (this.spinSource === src) this.spinSource = null; };
      }
    } catch (err) {
      console.warn(`[sound] play(${key}) failed`, err);
    }
  }

  /* ---------------- the nine, as intentions ---------------- */

  /** 1. Primary/dark button press. Ghost buttons stay silent — see Btn. */
  tap() {
    this.play('tap');
  }

  /** 2. The wheel starts turning. */
  spin() {
    this.play('spin');
  }

  /**
   * 3. The wheel settles. Cuts the 2.3s whirr short if the wheel stopped early —
   * a ratchet still whirring under the letter is the sound of a bug.
   */
  reveal() {
    this.stopSpin();
    this.play('reveal');
    this.haptic(10);
  }

  /**
   * Let the whirr run 80ms UNDER the thock and then stop, rather than chopping
   * it dead on the same frame (which clicks, and reads as a glitch). The wheel
   * settles into the letter; it doesn't get switched off.
   */
  stopSpin() {
    const src = this.spinSource;
    if (!src || !this.ctx) return;
    this.spinSource = null;
    try {
      src.stop(this.ctx.currentTime + 0.08);
    } catch {
      /* already ended — nothing to stop */
    }
  }

  /** 4. A keystroke in a race field. Throttled to 1 per 300ms PER FIELD. */
  scribble(field: string) {
    this.play('scribble', { throttleMs: 300, throttleKey: field });
  }

  /** 5. YOUR OWN "I'M DONE". Never fires for anybody else's lock-in. */
  lockin() {
    this.play('lockin');
    this.haptic(10);
  }

  /** 6. One of the last five seconds. (Ten ticks is nagging; five is a pulse.) */
  tick() {
    this.play('tick');
  }

  /**
   * 7. A tally row lands. Each consecutive pop is a semitone higher — the row
   * ladder is the sound of a score being counted up, and it is why the cascade
   * feels like an ascent rather than a list. Resets between categories.
   */
  pop() {
    const now = Date.now();
    if (now - this.popLastAt > POP_RESET_MS) this.popStep = 0;
    this.popLastAt = now;

    const step = Math.min(this.popStep, POP_MAX_STEPS - 1);
    // 2^(n/12) is one semitone per step; clamp so it can never shriek.
    const rate = Math.min(POP_MAX_RATE, Math.pow(2, step / 12));
    this.play('pop', { rate });
    this.popStep++;
  }

  /** Between categories. The next row is a new ladder, not the eighth rung. */
  resetPops() {
    this.popStep = 0;
  }

  /**
   * 8. The winner. ONE warm chord, once per GAME.
   *
   * Six phones playing a 1.2s pitched chord at the same instant is a smear, so
   * each device delays by a stable hash of its own player id — 0–250ms. The
   * room strums.
   */
  chord(playerId?: string) {
    const delayMs = playerId ? hash(playerId) % 250 : 0;
    this.play('chord', { delayMs });
    this.haptic([10, 40, 10], delayMs);
  }

  /** 9. An invite, a toast. Yours alone. */
  notify() {
    this.play('notify');
  }

  /* ---------------- haptics ---------------- */

  /**
   * Haptics pair with reveal, lockin and chord — the three moments that are
   * about a thing LANDING.
   *
   * `prefers-reduced-motion` is not about sound (a user who dislikes motion
   * still wants to hear the timer), so it does NOT gate audio. It absolutely
   * does gate haptics: a buzz in the hand is motion, and it is the one place
   * that preference has to be honoured here.
   */
  private haptic(pattern: number | number[], delayMs = 0) {
    if (!this.config.enabled || !this.config.hapticEnabled) return;
    if (this.isTv) return; // a television does not vibrate
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    if (prefersReducedMotion()) return;
    const fire = () => { try { navigator.vibrate(pattern); } catch {} };
    if (delayMs > 0) setTimeout(fire, delayMs);
    else fire();
  }
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Where a sound lives. Exported so a test can assert the file is really there. */
export function soundUrl(key: SoundKey): string {
  return `/sounds/${key}.wav`;
}

/** djb2. Stable across devices — every phone must compute the SAME offset for
 *  the same player, or the strum is just noise. */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h >>> 0;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const soundService = new SoundService();

/** Kept for the components that already call `useSound()`. */
export function useSound() {
  return soundService;
}
